import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Card, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { ISubmissionVisit, ISurvey } from '../../../constants/types';
import { useCurrentInvestigation } from '../../../hooks';
import { attachPhoto, fetchVisitPhotoPlacement, movePhoto } from '../../../services/lesionImagePlacement';
import { fetchSubmissionsPatientInvestigationAction } from '../../../redux/actions/submissionsPatientActions';
import { usePerImageDecrypt } from '../QualityCheck/usePerImageDecrypt';
import {
    describeLesions, lesionTitle, PhotoToPlace, placementTargets, UnassignedImageDto, VisitPhotoPlacementDto,
} from './photoPlacement';
import PhotoPlacementPanel, { PhotoPlacementState } from './PhotoPlacementPanel';
import PhotoPlacementDialog, { PlacementChoice } from './PhotoPlacementDialog';
import { mayCorrectVisit } from '../MyCorrections/correctionAccess';

const T = 'hospital.patient.photo-placement';

interface PlacementSubmission {
    id: number;
    uuidPatient: string;
    visit?: ISubmissionVisit | null;
    fieldGroups?: Parameters<typeof describeLesions>[1]['fieldGroups'];
}

interface PhotoPlacementSectionProps {
    uuidInvestigation: string;
    submission: PlacementSubmission;
    survey: ISurvey;
    /** after a move / attach: the submission's edit history has a new row */
    onPlaced: () => void;
    /** SKIN-600: the visits whose open flag is assigned to the caller (null: unknown or not asked). */
    ownOpenFlagVisits?: ReadonlySet<string> | null;
}

/**
 * SKIN-576 — re-filing a closed visit's photos, under its submission on the patient page.
 *
 * Offered only where the reviewer may correct the visit and the visit is SUBMITTED; closed until
 * opened, so the patient page decrypts nothing extra on load. Opening reads the visit's lesions
 * and unassigned photos (GET unassigned) and decrypts each photo in the browser. After a move or
 * attach it re-reads the patient's submissions (the record view), the placement and, through
 * `onPlaced`, the edit history.
 */
export default function PhotoPlacementSection({ uuidInvestigation, submission, survey, onPlaced, ownOpenFlagVisits = null }: PhotoPlacementSectionProps) {
    const { currentInvestigation } = useCurrentInvestigation();
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [version, setVersion] = useState(0);
    const [read, setRead] = useState<{ kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; placement: VisitPhotoPlacementDto }>({ kind: 'loading' });
    const [placing, setPlacing] = useState<PhotoToPlace | null>(null);

    // The API's canCorrectVisit (SKIN-599): APPROVE_VISIT, or the assignee of the visit's open flag.
    const offered = mayCorrectVisit(currentInvestigation, submission.visit, ownOpenFlagVisits);
    const uuidVisit = submission.visit?.uuid ?? '';

    useEffect(() => {
        if (!offered || !open) return undefined;
        let cancelled = false;
        setRead({ kind: 'loading' });
        fetchVisitPhotoPlacement(uuidInvestigation, uuidVisit).then(
            (placement) => { if (!cancelled) setRead({ kind: 'ready', placement }); },
            () => { if (!cancelled) setRead({ kind: 'error' }); },
        );
        return () => { cancelled = true; };
    }, [offered, open, version, uuidInvestigation, uuidVisit]);

    // Labelled from the submission as it is now, so a refreshed record relabels the lesions.
    const state: PhotoPlacementState = useMemo(() => (read.kind === 'ready'
        ? { kind: 'ready', lesions: describeLesions(read.placement.lesions, submission, survey), unassigned: read.placement.images }
        : read), [read, submission, survey]);

    // usePerImageDecrypt keys its answers by `encryptedFileUuid`; a lesion slot names its photos
    // by localId only, so every photo here is keyed by its localId (unique within a study).
    const decryptTargets = useMemo(() => {
        if (state.kind !== 'ready') return [];
        const localIds = [
            ...state.lesions.flatMap((lesion) => lesion.slots.flatMap((slot) => slot.localIds)),
            ...state.unassigned.map((image) => image.localId),
        ];
        return Array.from(new Set(localIds)).map((localId) => ({ encryptedFileUuid: localId, localId }));
    }, [state]);
    const { bytesByUuid: bytesByLocalId, retry } = usePerImageDecrypt(uuidInvestigation, decryptTargets);

    if (!offered) return null;

    const lesions = state.kind === 'ready' ? state.lesions : [];
    const source = placing?.kind === 'move'
        ? lesions.find((lesion) => lesion.groupRecordUuid === placing.fromGroupRecordUuid)
        : undefined;
    const sourceSlot = placing?.kind === 'move' ? source?.slots.find((slot) => slot.name === placing.field) : undefined;

    const place = async (photo: PhotoToPlace, choice: PlacementChoice, reason: string) => {
        const target = { localId: photo.localId, toGroupRecordUuid: choice.groupRecordUuid, field: choice.field, reason };
        if (photo.kind === 'move') {
            await movePhoto(uuidInvestigation, uuidVisit, { ...target, fromGroupRecordUuid: photo.fromGroupRecordUuid });
        } else {
            await attachPhoto(uuidInvestigation, uuidVisit, target);
        }
        setVersion((current) => current + 1);
        dispatch(fetchSubmissionsPatientInvestigationAction(uuidInvestigation, submission.uuidPatient) as any);
        onPlaced();
    };

    return (
        <Card variant="outlined" sx={{ p: 2, mb: 1 }} data-testid="photo-placement">
            <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="subtitle1" fontWeight={600}><Translate id={`${T}.title`} /></Typography>
                <Button size="small" variant="outlined" sx={{ ml: 'auto' }} data-testid="placement-open" onClick={() => setOpen((current) => !current)}>
                    <Translate id={open ? `${T}.close` : `${T}.open`} />
                </Button>
            </Box>
            {open && (
                <Box sx={{ mt: 2 }}>
                    <PhotoPlacementPanel state={state} bytesByLocalId={bytesByLocalId}
                        onRetry={() => setVersion((current) => current + 1)}
                        onRetryImage={retry}
                        onMove={setPlacing}
                        onAttach={(image: UnassignedImageDto) => setPlacing({ kind: 'attach', localId: image.localId })} />
                </Box>
            )}
            {placing && (
                <PhotoPlacementDialog key={`${placing.kind}-${placing.localId}`}
                    photo={placing}
                    bytes={bytesByLocalId[placing.localId]}
                    sourceLabel={source && sourceSlot ? `${lesionTitle(source)} · ${sourceSlot.label}` : undefined}
                    targets={placementTargets(lesions, placing)}
                    onSubmit={(choice, reason) => place(placing, choice, reason)}
                    onClose={() => setPlacing(null)} />
            )}
        </Card>
    );
}
